<template>
  <div
    class="weekly-activity-assignments-table"
    :class="[
      `weekly-activity-assignments-table--${theme}`,
      { 'weekly-activity-assignments-table--activity-mode': isActivityRootView },
    ]"
  >
    <div v-if="showToolbar" class="weekly-activity-assignments-table__toolbar">
      <div class="flex items-center gap-2 flex-wrap">
        <span v-if="level != null" class="weekly-activity-assignments-table__level-badge">
          {{ $t('weekly_assignments_level', { level }) }}
        </span>
        <span v-if="!showSectionHeader" class="weekly-activity-assignments-table__count">
          {{ displayCount }} {{ $t(countLabelKey) }}
        </span>
        <button
          v-if="showActivityAddButton"
          type="button"
          class="weekly-activity-assignments-table__add-btn weekly-activity-assignments-table__add-btn--engineering"
          @click="openEngineeringMeasurementModal"
        >
          <b-icon pack="fas" icon="plus" size="is-small" />
          <span>{{ $t('weekly_add_engineering_measurement') }}</span>
        </button>
      </div>
    </div>

    <div class="weekly-activity-assignments-table__body">
      <slot>
        <div
          class="weekly-activity-assignments-table__section"
          :class="{ 'weekly-activity-assignments-table__section--nested': !showSectionHeader }"
        >
          <div
            v-if="showSectionHeader"
            class="weekly-activity-assignments-table__section-header"
          >
            <b-icon
              pack="fas"
              icon="ruler-combined"
              size="is-small"
              class="weekly-activity-assignments-table__section-icon"
            />
            <span class="weekly-activity-assignments-table__section-title">
              {{ $t('weekly_assignments_associated_title', { count: displayCount }) }}
            </span>
          </div>

          <!-- Edición de mediciones (ponderación / unidades), mismo comportamiento que activity-assignments -->
          <div v-if="isWeightEditing" class="weekly-activity-assignments-table__edit-bar">
            <div class="weekly-activity-assignments-table__edit-total">
              <span :class="isWeightTotalValid ? 'text-green-dark' : 'text-red'">
                {{ $t('weighting_total', { total: weightTotal }) }}
              </span>
              <span v-if="!isWeightTotalValid" class="text-red">· {{ $t('weighting_not_100') }}</span>
            </div>
            <div class="flex items-center gap-2">
              <b-button
                type="is-small is-primary"
                class="font-semibold uppercase"
                :disabled="savingWeights || !weightEditableRows.length"
                @click="distributeMeasurementWeights"
              >
                {{ $t('distribute_weights') }}
              </b-button>
              <b-button
                type="is-small is-success"
                class="font-semibold uppercase"
                :loading="savingWeights"
                :disabled="!isWeightTotalValid"
                @click="saveEditedMeasurements"
              >
                {{ $t('save') }}
              </b-button>
            </div>
          </div>

          <!-- Eliminación múltiple: se marcan las mediciones y se borran todas tras confirmar -->
          <div v-if="isDeleteSelecting" class="weekly-activity-assignments-table__delete-bar">
            <div class="flex items-center gap-3 flex-wrap">
              <b-checkbox
                :value="allDeletableSelected"
                :indeterminate="someDeletableSelected"
                :disabled="deletingSelected || !deletableRows.length"
                type="is-danger"
                size="is-small"
                @input="toggleSelectAllForDelete($event)"
              >
                <span class="text-xs font-semibold">{{ $t('select_all') }}</span>
              </b-checkbox>
              <span class="weekly-activity-assignments-table__delete-count">
                {{ $t('selected_elements_count', [selectedDeleteIds.length]) }}
              </span>
            </div>
            <b-button
              type="is-small is-danger"
              class="font-semibold uppercase"
              icon-pack="fas"
              icon-left="trash-alt"
              :loading="deletingSelected"
              :disabled="!selectedDeleteIds.length"
              @click="confirmDeleteSelected"
            >
              {{ $t('delete_selected_assignments') }} ({{ selectedDeleteIds.length }})
            </b-button>
          </div>

          <div class="overflow-x-auto">
          <table class="weekly-activity-assignments-table__grid w-full text-xs border-collapse min-w-max">
            <thead>
              <tr>
                <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__expand-col" />
                <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__actions-col" />
                <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_table_slot') }}</th>
                <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('measurement_code') }}</th>
                <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('name') }}</th>
                <th v-if="showWeightColumn" class="weekly-activity-assignments-table__header">{{ $t('weighting') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('ud') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_scheduled_quantity') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_theoretical_kpi') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_executed_quantity') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_accumulated_hours') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_real_kpi') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('progress') }}</th>
                <th class="weekly-activity-assignments-table__header">{{ $t('completed') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingData && !displayRows.length">
                <td :colspan="columnCount" class="weekly-activity-assignments-table__cell px-4 py-6 text-center text-gray-500">
                  <b-loading :active="true" :is-full-page="false" />
                </td>
              </tr>
              <tr v-else-if="!displayRows.length">
                <td :colspan="columnCount" class="weekly-activity-assignments-table__cell px-4 py-6 text-center text-gray-500">
                  {{ $t('no_results') }}
                </td>
              </tr>
              <template v-for="(assignment, assignmentIndex) in displayRows">
                <tr
                  :key="assignmentRowKey(assignment, assignmentIndex) + '-row'"
                  class="hover:bg-blue-lightest"
                  :class="{
                    'weekly-activity-assignments-table__row--expanded': isAssignmentExpanded(assignment, assignmentIndex),
                    'weekly-activity-assignments-table__row--imported': isImportedBoqSource(assignment),
                  }"
                >
                  <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__expand-col text-center">
                    <button
                      v-if="assignmentHasChildren(assignment)"
                      type="button"
                      class="inline-flex items-center justify-center w-7 h-7 text-blue-light hover:text-blue focus:outline-none"
                      :aria-expanded="isAssignmentExpanded(assignment, assignmentIndex)"
                      :aria-label="isAssignmentExpanded(assignment, assignmentIndex)
                        ? $t('weekly_assignment_hide_sub_measurements')
                        : $t('weekly_assignment_show_sub_measurements')"
                      @click="toggleAssignmentDetail(assignment, assignmentIndex)"
                    >
                      <b-icon
                        pack="fas"
                        :icon="isAssignmentExpanded(assignment, assignmentIndex) ? 'chevron-down' : 'chevron-right'"
                        size="is-small"
                      />
                    </button>
                  </td>
                  <!-- Acciones -->
                  <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__actions-col text-center">
                    <template v-if="isDeleteSelecting">
                      <b-checkbox
                        v-if="canDeleteAssignment(assignment)"
                        :value="isSelectedForDelete(assignment)"
                        :disabled="deletingSelected"
                        type="is-danger"
                        size="is-small"
                        class="weekly-activity-assignments-table__delete-check"
                        @input="toggleSelectedForDelete(assignment, $event)"
                      />
                    </template>
                    <div v-else class="text-center cursor-pointer" @click.stop>
                      <b-dropdown aria-role="list" position="is-bottom-right" append-to-body>
                        <template #trigger="{ active }">
                          <button
                            type="button"
                            class="inline-flex items-center justify-center w-7 h-7 text-grey-dark hover:text-blue focus:outline-none"
                            :aria-label="$t('actions')"
                          >
                            <b-icon pack="fas" :icon="active ? 'ellipsis-h' : 'ellipsis-v'" size="is-small" />
                          </button>
                        </template>
                        <b-dropdown-item
                          v-if="canRegisterProgress(assignment) && !isFreeBoqRootMode"
                          aria-role="listitem"
                          @click="openRegisterProgressModal(assignment)"
                        >
                          <span class="text-xs">{{ $t('register_progress') }}</span>
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="canRegisterProgress(assignment) && isFreeBoqRootMode"
                          aria-role="listitem"
                          @click="openContributeDrawingModal(assignment)"
                        >
                          <span class="text-xs">{{ registerProgressLabel }}</span>
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <div class="weekly-activity-assignments-table__slot">
                      <b-icon
                        v-if="hasBoqRootSource(assignment)"
                        pack="fas"
                        :icon="assignmentSlotIcon(assignment)"
                        size="is-small"
                        class="weekly-activity-assignments-table__slot-icon"
                        :class="assignmentSlotIconClass(assignment)"
                      />
                      <span
                        class="weekly-activity-assignments-table__slot-badge"
                        :class="assignmentSlotBadgeClass(assignment)"
                      >
                        {{ assignmentTypeLabel(assignment) }}
                      </span>
                    </div>
                  </td>
                  <td class="weekly-activity-assignments-table__cell whitespace-nowrap">
                    <span v-if="isEmpty(assignment.code)" class="weekly-activity-assignments-table__empty">—</span>
                    <a v-else href="#" class="weekly-activity-assignments-table__code-link" @click.prevent>
                      {{ assignment.code }}
                    </a>
                  </td>
                  <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__name-cell">
                    <span class="weekly-activity-assignments-table__name-text">{{ assignmentDescription(assignment) }}</span>
                  </td>
                  <td v-if="showWeightColumn" class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <b-input
                      v-if="isWeightEditing && isWeightEditableRow(assignment)"
                      v-model="assignment.weight"
                      type="number"
                      size="is-small"
                      min="0"
                      max="100"
                      step="0.01"
                      class="weekly-activity-assignments-table__edit-input"
                      @input="onEditMeasurement(assignment, 'weight')"
                    />
                    <span v-else-if="isEmpty(assignment.weight)" class="weekly-activity-assignments-table__empty">—</span>
                    <span v-else>{{ formatQuantity(assignment.weight) }}%</span>
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    {{ assignmentUnit(assignment) }}
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <b-input
                      v-if="isWeightEditing && canEditTotalUnits(assignment)"
                      v-model="assignment.total_units"
                      type="number"
                      size="is-small"
                      min="0"
                      step="0.01"
                      class="weekly-activity-assignments-table__edit-input"
                      @input="onEditMeasurement(assignment, 'total_units')"
                    />
                    <template v-else>
                      <span v-if="isEmpty(assignmentTotal(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentTotal(assignment)) }}</span>
                    </template>
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <span v-if="isEmpty(theoreticalKpi(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                    <span v-else class="weekly-activity-assignments-table__kpi-theoretical">{{ formatQuantity(theoreticalKpi(assignment), 2) }}</span>
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <span v-if="isEmpty(assignment.executed_quantity)" class="weekly-activity-assignments-table__empty">—</span>
                    <span v-else>{{ formatQuantity(assignment.executed_quantity) }}</span>
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <span v-if="isEmpty(accumulatedHours(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                    <span v-else>{{ formatQuantity(accumulatedHours(assignment), 2) }} h</span>
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                    <span v-if="isEmpty(realKpi(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                    <span v-else class="weekly-activity-assignments-table__kpi-real">{{ formatQuantity(realKpi(assignment), 2) }}</span>
                  </td>
                  <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__progress-cell">
                    <progress-bar
                      :step="assignmentProgress(assignment)"
                      color="blue-light"
                      class="w-full"
                    />
                  </td>
                  <td class="weekly-activity-assignments-table__cell text-center">
                    <b-icon
                      v-if="assignment.completed"
                      pack="fas"
                      icon="check"
                      size="is-small"
                      class="weekly-activity-assignments-table__complete-icon weekly-activity-assignments-table__complete-icon--yes"
                    />
                    <b-icon
                      v-else
                      pack="fas"
                      icon="times"
                      size="is-small"
                      class="weekly-activity-assignments-table__complete-icon weekly-activity-assignments-table__complete-icon--no"
                    />
                  </td>
                </tr>
                <tr
                  v-if="assignmentHasChildren(assignment) && isAssignmentExpanded(assignment, assignmentIndex)"
                  :key="`${assignmentRowKey(assignment, assignmentIndex)}-detail`"
                  class="weekly-activity-assignments-table__detail-row"
                >
                  <td :colspan="columnCount" class="p-0 weekly-activity-assignments-table__detail-cell">
                    <div class="weekly-activity-assignments-table__detail-panel">
                      <weekly-activity-assignments-table
                        :show-toolbar="false"
                        :activity-id="activityId"
                        :parent-assignment-id="assignment.id"
                        :progress-type-mode="progressTypeMode"
                        :theme="theme"
                        :activity-start-date="activityStartDate"
                        :activity-end-date="activityEndDate"
                        :activity-people-limit="activityPeopleLimit"
                      />
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          </div>
        </div>
      </slot>
    </div>

    <weekly-register-progress-modal ref="weeklyRegisterProgressModal" @saved="onProgressRegistered" />
    <!-- Modo free: mismo modal de registro de progreso que assignment-table (vista de actividades) -->
    <assignment-contribute-drawing-modal
      v-if="isFreeBoqRootMode && activityId"
      ref="assignmentContributeDrawingModal"
      entity-type="activity"
      :entity-id="Number(activityId)"
      :current-start-date="activityStartDate || ''"
      :current-end-date="activityEndDate || ''"
    />
    <weekly-associate-engineering-measurement-modal
      ref="weeklyAssociateEngineeringModal"
      :project-id="projectId"
      @created="onEngineeringMeasurementCreated"
    />
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { quantityDisplayDecimals } from '·/helpers/quantity-display'
import EventBus from '~/plugins/bus'
import WeeklyRegisterProgressModal from './weekly-register-progress-modal.vue'
import WeeklyAssociateEngineeringMeasurementModal from './weekly-associate-engineering-measurement-modal.vue'
import AssignmentContributeDrawingModal from '·/components/assignments/assignment-contribute-drawing-modal.vue'
import {
  readWeeklyUiSession,
  writeWeeklyUiSession,
} from './weekly-ui-session.js'
import {
  activityDurationInWorkingDays,
  fetchProjectHolidayDates,
  resolveWorkDays,
} from './projectCalendarUtils.js'

/** Días de calendario inclusive (KPI teórico / transcurridos; no calendario laborable). */
function resolveDurationDays(startDate, endDate) {
  if (!startDate || !endDate) {
    return null
  }

  const start = moment(startDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).startOf('day')
  const end = moment(endDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).startOf('day')
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return null
  }

  return Math.abs(end.diff(start, 'days')) + 1
}

const ASSIGNMENT_LINE_STATUS_OK = 0
export default {
  name: 'weekly-activity-assignments-table',

  components: {
    WeeklyRegisterProgressModal,
    WeeklyAssociateEngineeringMeasurementModal,
    AssignmentContributeDrawingModal,
  },

  props: {
    /** Actividad o subactividad: assignable_id de las mediciones (GET /api/v2/activity/{id}/assignments). */
    activityId: {
      type: [Number, String],
      default: null,
    },
    activityName: {
      type: String,
      default: null,
    },
    projectId: {
      type: Number,
      default: null,
    },
    /** Medición padre cuando se listan sub-mediciones (instancia recursiva). */
    parentAssignmentId: {
      type: [Number, String],
      default: null,
    },
    /** 'activity' muestra el botón de medición de ingeniería en la raíz; 'subactivity' no muestra botón. */
    progressTypeMode: {
      type: String,
      default: 'subactivity',
      validator: (value) => ['activity', 'subactivity'].includes(value),
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    level: {
      type: Number,
      default: null,
    },
    count: {
      type: Number,
      default: 0,
    },
    countLabelKey: {
      type: String,
      default: 'assignments',
    },
    showAddButton: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: 'blue',
      validator: (value) => ['blue', 'green'].includes(value),
    },
    activityStartDate: {
      type: String,
      default: null,
    },
    activityEndDate: {
      type: String,
      default: null,
    },
    activityPeopleLimit: {
      type: [Number, String],
      default: null,
    },
    /** Edición de ponderación / unidades de las mediciones raíz (lo activa el padre). */
    weightEditMode: {
      type: Boolean,
      default: false,
    },
    /** Muestra checks en las mediciones raíz que el usuario puede eliminar (lo activa el padre). */
    deleteMode: {
      type: Boolean,
      default: false,
    },
    /** Gestor del proyecto: puede borrar cualquier medición, no solo las suyas. */
    canManage: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['created', 'weight-edit-saved', 'deleted'],

  data() {
    return {
      assignments: [],
      loadingData: false,
      expandedAssignmentKeys: [],
      silentRefreshTimer: null,
      projectHolidayDates: new Set(),
      // Valores originales por id para restaurar al cancelar la edición
      weightEditSnapshot: {},
      editedMeasurements: [],
      savingWeights: false,
      selectedDeleteIds: [],
      deletingSelected: false,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
      company: 'app/company',
      user: 'auth/user',
    }),

    /** Modo libre (configs.boq_root = free en proyecto semanal): mediciones clásicas de activities. */
    isFreeBoqRootMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type === 'weekly' && configs.boq_root === 'free')
    },

    /** Mismo texto que assignment-table. */
    registerProgressLabel() {
      if (this.company && this.company.alias === 'welink') {
        return this.$t('progress_to_inspect')
      }
      return this.$t('register_progress')
    },

    projectWorkDays() {
      return resolveWorkDays(this.project && this.project.configs)
    },

    projectManHour() {
      const configs = this.project && this.project.configs
      const raw = configs && configs.man_hour != null && configs.man_hour !== ''
        ? Number(configs.man_hour)
        : null

      if (raw != null && !Number.isNaN(raw) && raw > 0) {
        return raw
      }
      return 8
    },

    isActivityRootView() {
      return this.progressTypeMode === 'activity' && this.parentAssignmentId == null
    },

    showActivityAddButton() {
      return this.isActivityRootView && this.showAddButton
    },

    showSectionHeader() {
      return this.parentAssignmentId == null
    },

    displayRows() {
      return this.assignments
    },

    displayCount() {
      if (this.count > 0) {
        return this.count
      }
      return this.displayRows.length
    },

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    /** Solo el nivel raíz de mediciones de la actividad es editable. */
    isWeightEditing() {
      return this.weightEditMode && this.parentAssignmentId == null
    },

    weightEditableRows() {
      return this.displayRows.filter((assignment) => this.isWeightEditableRow(assignment))
    },

    weightTotal() {
      const total = this.weightEditableRows.reduce((sum, assignment) => {
        const weight = Number(assignment.weight)
        return sum + (Number.isNaN(weight) ? 0 : weight)
      }, 0)
      return Math.round(total * 100) / 100
    },

    isWeightTotalValid() {
      return this.weightTotal === 100
    },

    /** Solo el nivel raíz admite selección para borrar (al borrar una medición se borran sus hijas). */
    isDeleteSelecting() {
      return this.deleteMode && this.parentAssignmentId == null
    },

    deletableRows() {
      return this.displayRows.filter((assignment) => this.canDeleteAssignment(assignment))
    },

    allDeletableSelected() {
      return this.deletableRows.length > 0 && this.selectedDeleteIds.length === this.deletableRows.length
    },

    someDeletableSelected() {
      return this.selectedDeleteIds.length > 0 && !this.allDeletableSelected
    },

    /** En modo free la ponderación siempre es visible; fuera de él solo mientras se editan mediciones. */
    showWeightColumn() {
      return this.isFreeBoqRootMode || this.isWeightEditing
    },

    columnCount() {
      return this.showWeightColumn ? 14 : 13
    },
  },

  watch: {
    isWeightEditing(editing) {
      if (editing) {
        this.startWeightEdit()
      } else {
        this.cancelWeightEdit()
      }
    },
    isDeleteSelecting() {
      this.selectedDeleteIds = []
    },
    assignments() {
      // Tras recargar, descarta seleccionadas que ya no existen o no se pueden borrar
      const deletableIds = this.deletableRows.map((assignment) => assignment.id)
      this.selectedDeleteIds = this.selectedDeleteIds.filter((id) => deletableIds.includes(id))
    },
    activityId() {
      this.reloadAssignments()
    },
    parentAssignmentId() {
      this.reloadAssignments()
    },
    effectiveProjectId: {
      immediate: true,
      handler(projectId) {
        if (projectId) {
          this.loadProjectCalendar()
        }
      },
    },
  },

  mounted() {
    this.reloadAssignments()
    this._onWeeklyAssignmentsSilentRefresh = () => this.scheduleSilentReload()
    EventBus.$on('weeklyAssignmentsSilentRefresh', this._onWeeklyAssignmentsSilentRefresh)
  },

  beforeDestroy() {
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer)
      this.silentRefreshTimer = null
    }
    EventBus.$off('weeklyAssignmentsSilentRefresh', this._onWeeklyAssignmentsSilentRefresh)
    this.releaseContributeDrawingWatchers()
  },

  methods: {
    async loadProjectCalendar() {
      const projectId = this.effectiveProjectId
      if (!projectId) {
        this.projectHolidayDates = new Set()
        return
      }
      const holidays = await fetchProjectHolidayDates(projectId)
      // Evita race si el proyecto cambió mientras llegaba la respuesta.
      if (String(this.effectiveProjectId) !== String(projectId)) {
        return
      }
      this.projectHolidayDates = holidays
    },

    /** Días laborables de una línea (work_days + festivos), mínimo 1. */
    lineWorkingDays(startDate, endDate) {
      return activityDurationInWorkingDays(
        startDate,
        endDate,
        this.projectWorkDays,
        this.projectHolidayDates || new Set()
      )
    },

    scheduleSilentReload() {
      if (this.silentRefreshTimer) {
        clearTimeout(this.silentRefreshTimer)
      }
      this.silentRefreshTimer = setTimeout(() => {
        this.silentRefreshTimer = null
        this.silentReloadAssignments()
      }, 150)
    },

    async silentReloadAssignments() {
      // Recargar en edición reemplazaría las filas y perdería los valores escritos
      if (!this.activityId || this.isWeightEditing) {
        return
      }
      await this.reloadAssignments({ silent: true })
      await Promise.all(
        this.assignments.map((assignment, assignmentIndex) => {
          if (!this.isAssignmentExpanded(assignment, assignmentIndex)) {
            return Promise.resolve()
          }
          if (!this.assignmentHasChildren(assignment)) {
            return Promise.resolve()
          }
          return this.loadAssignments(this.activityId, assignment.id, assignment, { silent: true })
        })
      )
    },

    reloadAssignments(options = {}) {
      if (!this.activityId) {
        return Promise.resolve()
      }
      if (this.parentAssignmentId != null) {
        return this.loadAssignments(this.activityId, this.parentAssignmentId, null, options)
      }
      return this.loadAssignments(this.activityId, null, null, options)
    },

    loadAssignments(id, parentAssignmentId = null, row = null, options = {}) {
      const silent = options.silent === true
      if (!silent) {
        this.loadingData = true
      }
      const url = `/api/v2/activity/${id}/assignments`
      const params = {
        with_boq_root: 1,
      }
      if (parentAssignmentId) {
        params.parent_assignment_id = parentAssignmentId
      }
      return axios.get(url, { params })
        .then((response) => {
          if (response.data && response.data.success) {
            const loaded = response.data.data || []
            if (row) {
              this.$set(row, 'child', loaded)
            } else {
              this.assignments = loaded
            }
          }
        })
        .catch(() => {
          // silent refresh keeps previous data visible
        })
        .finally(() => {
          if (!silent) {
            this.loadingData = false
          }
          if (!row) {
            this.$nextTick(() => {
              this.restoreAssignmentsFromSession()
            })
          }
        })
    },

    persistAssignmentsSession() {
      const projectId = this.effectiveProjectId
      if (!projectId || !this.activityId) {
        return
      }

      const state = readWeeklyUiSession(projectId)
      const activityPrefix = `${this.activityId}:`
      const levelPrefix = `${this.parentAssignmentId || 'root'}-`

      const kept = state.assignments.filter((storedKey) => {
        if (!String(storedKey).startsWith(activityPrefix)) {
          return true
        }
        return !String(storedKey).slice(activityPrefix.length).startsWith(levelPrefix)
      })

      this.expandedAssignmentKeys.forEach((localKey) => {
        kept.push(`${activityPrefix}${localKey}`)
      })

      state.assignments = kept
      writeWeeklyUiSession(projectId, state)
    },

    async restoreAssignmentsFromSession() {
      const projectId = this.effectiveProjectId
      if (!projectId || !this.activityId || !this.assignments.length) {
        return
      }

      const state = readWeeklyUiSession(projectId)
      const activityPrefix = `${this.activityId}:`
      const levelPrefix = `${this.parentAssignmentId || 'root'}-`

      this.expandedAssignmentKeys = state.assignments
        .filter((storedKey) => {
          if (!String(storedKey).startsWith(activityPrefix)) {
            return false
          }
          return String(storedKey).slice(activityPrefix.length).startsWith(levelPrefix)
        })
        .map((storedKey) => String(storedKey).slice(activityPrefix.length))

      await Promise.all(
        this.assignments.map((assignment, assignmentIndex) => {
          if (!this.isAssignmentExpanded(assignment, assignmentIndex)) {
            return Promise.resolve()
          }
          if (!this.assignmentHasChildren(assignment)) {
            return Promise.resolve()
          }
          return this.loadAssignments(this.activityId, assignment.id, assignment, { silent: true })
        })
      )
    },

    assignmentRowKey(assignment, assignmentIndex) {
      return assignment.id != null ? assignment.id : assignmentIndex
    },

    assignmentExpandKey(assignment, assignmentIndex) {
      return `${this.parentAssignmentId || 'root'}-${this.assignmentRowKey(assignment, assignmentIndex)}`
    },

    assignmentHasChildren(assignment) {
      return Number(assignment.children_count || 0) > 0
    },

    isAssignmentExpanded(assignment, assignmentIndex) {
      return this.expandedAssignmentKeys.includes(
        this.assignmentExpandKey(assignment, assignmentIndex)
      )
    },

    toggleAssignmentDetail(assignment, assignmentIndex) {
      const key = this.assignmentExpandKey(assignment, assignmentIndex)
      if (this.isAssignmentExpanded(assignment, assignmentIndex)) {
        this.expandedAssignmentKeys = this.expandedAssignmentKeys.filter((k) => k !== key)
        this.persistAssignmentsSession()
        return
      }
      this.expandedAssignmentKeys = [...this.expandedAssignmentKeys, key]
      this.loadAssignments(this.activityId, assignment.id, assignment).then(() => {
        this.persistAssignmentsSession()
      })
    },

    assignmentTypeLabel(assignment) {
      if (!assignment.type) {
        return this.$t('assignment')
      }
      if (this.parentAssignmentId != null && assignment.type === 'resource') {
        return this.$t('sub_assignment')
      }
      if (assignment.type === 'resource') {
        return this.$t('assignment')
      }
      return this.$t(assignment.type)
    },

    hasBoqRootSource(assignment) {
      if (assignment.boq_root_source === 'imported' || assignment.boq_root_source === 'manual') {
        return true
      }
      return this.isActivityRootView && (!assignment.type || assignment.type === 'resource')
    },

    isImportedBoqSource(assignment) {
      return assignment.boq_root_source === 'imported'
    },

    assignmentSlotIcon(assignment) {
      return this.isImportedBoqSource(assignment) ? 'calendar-alt' : 'ruler-combined'
    },

    assignmentSlotIconClass(assignment) {
      return this.isImportedBoqSource(assignment)
        ? 'weekly-activity-assignments-table__slot-icon--imported'
        : 'weekly-activity-assignments-table__slot-icon--manual'
    },

    assignmentSlotBadgeClass(assignment) {
      if (!this.hasBoqRootSource(assignment)) {
        return ''
      }
      return this.isImportedBoqSource(assignment)
        ? 'weekly-activity-assignments-table__slot-badge--imported'
        : 'weekly-activity-assignments-table__slot-badge--manual'
    },

    assignmentDescription(assignment) {
      return assignment.description || assignment.label || assignment.name || '—'
    },

    assignmentUnit(assignment) {
      return assignment.unit_name || assignment.unit || '—'
    },

    assignmentTotal(assignment) {
      if (assignment.type === 'task') {
        return assignment.weight
      }
      if (assignment.total != null && assignment.total !== '') {
        return assignment.total
      }
      return assignment.scheduled_quantity
    },

    /** Días desde current_start_date hasta hoy (o current_end_date si ya pasó). */
    activityTheoreticalKpiDays() {
      if (!this.activityStartDate || !this.activityEndDate) {
        return null
      }

      const start = moment(this.activityStartDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).startOf('day')
      const activityEnd = moment(this.activityEndDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).startOf('day')
      const today = moment().startOf('day')

      if (!start.isValid() || !activityEnd.isValid()) {
        return null
      }

      const end = today.isAfter(activityEnd) ? activityEnd : today

      return resolveDurationDays(
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD')
      )
    },

    /** KPI teórico = cantidad programada / días transcurridos de la actividad. */
    theoreticalKpi(assignment) {
      return this.assignmentKpi(assignment, this.assignmentTotal(assignment))
    },

    /** KPI real = cantidad ejecutada / días transcurridos de la actividad. */
    realKpi(assignment) {
      return this.assignmentKpi(assignment, assignment.executed_quantity)
    },

    assignmentKpi(assignment, quantity) {
      const parsedQuantity = parseFloat(quantity)
      if (Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
        return null
      }

      const days = this.activityTheoreticalKpiDays()
      if (!days || days <= 0) {
        return null
      }

      return parsedQuantity / days
    },

    /** HH acumuladas: suma de líneas OK → people × días laborables × man_hour del proyecto. */
    accumulatedHours(assignment) {
      if (!assignment || !Array.isArray(assignment.lines) || !assignment.lines.length) {
        return null
      }

      const manHour = this.projectManHour
      let total = 0
      let hasLines = false

      assignment.lines.forEach((line) => {
        if (line.status !== ASSIGNMENT_LINE_STATUS_OK) {
          return
        }
        if (!line.start_date || !line.end_date) {
          return
        }

        const people = parseInt(line.people, 10)
        if (Number.isNaN(people) || people <= 0) {
          return
        }

        const days = this.lineWorkingDays(line.start_date, line.end_date)
        if (days <= 0) {
          return
        }

        hasLines = true
        total += people * days * manHour
      })

      if (!hasLines) {
        return null
      }

      return Number(total.toFixed(5))
    },

    assignmentProgress(assignment) {
      if (assignment.type === 'task') {
        return parseFloat(assignment.executed_quantity) || 0
      }
      const total = parseFloat(assignment.total)
      if (!total || total <= 0) {
        return parseFloat(assignment.progress) || 0
      }
      return (parseFloat(assignment.executed_quantity) * 100) / total
    },

    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },

    formatQuantity(value, decimals) {
      const num = parseFloat(value)
      if (Number.isNaN(num)) {
        return value
      }
      const scale = decimals == null ? quantityDisplayDecimals(this.project) : decimals
      return num.toFixed(scale)
    },

    canRegisterProgress(assignment) {
      if (!assignment || assignment.completed) {
        return false
      }
      if (assignment.type && assignment.type !== 'resource') {
        return false
      }
      return Number(assignment.children_count || 0) === 0
    },

    openEngineeringMeasurementModal() {
      if (!this.isActivityRootView || !this.$refs.weeklyAssociateEngineeringModal) {
        return
      }
      this.$refs.weeklyAssociateEngineeringModal.show({
        activity: {
          id: this.activityId,
          name: this.activityName,
        },
      })
    },

    onEngineeringMeasurementCreated() {
      this.reloadAssignments()
      this.$emit('created', { activity_id: this.activityId })
    },

    onProgressRegistered() {
      this.silentReloadAssignments()
    },

    openRegisterProgressModal(assignment) {
      if (!this.$refs.weeklyRegisterProgressModal || !this.canRegisterProgress(assignment)) {
        return
      }
      this.$refs.weeklyRegisterProgressModal.show({
        assignment,
        activityId: this.activityId,
        activityStartDate: this.activityStartDate,
        activityEndDate: this.activityEndDate,
        activityPeople: this.activityPeopleLimit,
      })
    },

    /** Mismas mediciones que "editar recursos" en activity-assignments: recursos de la actividad. */
    isWeightEditableRow(assignment) {
      return !!assignment && (!assignment.type || assignment.type === 'resource')
    },

    /** Las unidades de una medición con hijas salen de las hijas. */
    canEditTotalUnits(assignment) {
      return this.isWeightEditableRow(assignment) && !this.assignmentHasChildren(assignment)
    },

    startWeightEdit() {
      const snapshot = {}
      this.displayRows.forEach((assignment) => {
        snapshot[assignment.id] = {
          weight: assignment.weight,
          total_units: assignment.total_units,
        }
      })
      this.weightEditSnapshot = snapshot
      this.editedMeasurements = []
    },

    cancelWeightEdit() {
      this.displayRows.forEach((assignment) => {
        const original = this.weightEditSnapshot[assignment.id]
        if (original) {
          assignment.weight = original.weight
          assignment.total_units = original.total_units
        }
      })
      this.weightEditSnapshot = {}
      this.editedMeasurements = []
    },

    /** Solo se envían los campos modificados (igual que onEditResource de activity-assignments). */
    onEditMeasurement(assignment, field) {
      if (!['weight', 'total_units'].includes(field)) {
        return
      }
      const edited = this.editedMeasurements.find((item) => item.id === assignment.id)
      if (edited) {
        edited[field] = assignment[field]
      } else {
        this.editedMeasurements.push({ id: assignment.id, [field]: assignment[field] })
      }
    },

    /** Reparte 100 en partes iguales; el último absorbe el redondeo (igual que distributeTaskWeights). */
    distributeMeasurementWeights() {
      const rows = this.weightEditableRows
      const count = rows.length
      if (!count) {
        return
      }
      const base = Math.floor((100 / count) * 100) / 100
      let sum = 0
      rows.forEach((assignment, index) => {
        if (index < count - 1) {
          assignment.weight = base
          sum += base
        } else {
          assignment.weight = Math.round((100 - sum) * 100) / 100
        }
        this.onEditMeasurement(assignment, 'weight')
      })
    },

    async saveEditedMeasurements() {
      if (!this.isWeightTotalValid || this.savingWeights) {
        return
      }
      if (!this.editedMeasurements.length) {
        this.$emit('weight-edit-saved', { activityId: this.activityId, changed: false })
        return
      }

      this.savingWeights = true
      try {
        const { data } = await axios.put(`/api/v2/assignments/edit/activity/${this.activityId}`, {
          assignment_resources: this.editedMeasurements,
        })
        if (!(data && data.success)) {
          this.$toast.open({ message: this.$t('error_saving'), type: 'is-danger', position: 'is-top-right' })
          return
        }
        this.$toast.open({ message: this.$t('save_successful'), type: 'is-success', position: 'is-top-right' })
        // Guardado: los valores nuevos no deben restaurarse al salir del modo edición
        this.weightEditSnapshot = {}
        this.editedMeasurements = []
        await this.reloadAssignments({ silent: true })
        this.$emit('weight-edit-saved', { activityId: this.activityId, changed: true })
      } catch (error) {
        this.$toast.open({ message: this.$t('error_saving'), type: 'is-danger', position: 'is-top-right' })
      } finally {
        this.savingWeights = false
      }
    },

    /**
     * assignment-contribute-drawing-modal no emite evento al guardar: se detecta el guardado por el
     * EventBus que lanza assignment-contribute y se refresca al cerrarse el modal.
     */
    openContributeDrawingModal(assignment) {
      const modal = this.$refs.assignmentContributeDrawingModal
      if (!modal || !this.canRegisterProgress(assignment)) {
        return
      }
      this.releaseContributeDrawingWatchers()

      let saved = false
      const onSaved = () => {
        saved = true
      }
      EventBus.$on('refreshActivityDocumentsNotifications', onSaved)

      modal.show(assignment.id, false)

      const unwatch = modal.$watch('isModalActive', (active) => {
        if (active) {
          return
        }
        this.releaseContributeDrawingWatchers()
        if (saved) {
          this.notifyContributeProgressSaved(assignment)
        }
      })

      this._releaseContributeDrawing = () => {
        unwatch()
        EventBus.$off('refreshActivityDocumentsNotifications', onSaved)
      }
    },

    /** Igual que assignment-update-modal: quien creó la medición o el gestor del proyecto. */
    canDeleteAssignment(assignment) {
      if (!assignment || assignment.id == null) {
        return false
      }
      if (this.canManage) {
        return true
      }
      const ownerId = assignment.user && assignment.user.user_id
      return ownerId != null && !!this.user && Number(ownerId) === Number(this.user.id)
    },

    isSelectedForDelete(assignment) {
      return !!assignment && this.selectedDeleteIds.includes(assignment.id)
    },

    toggleSelectedForDelete(assignment, checked) {
      if (!this.canDeleteAssignment(assignment)) {
        return
      }
      const others = this.selectedDeleteIds.filter((id) => id !== assignment.id)
      this.selectedDeleteIds = checked ? [...others, assignment.id] : others
    },

    toggleSelectAllForDelete(checked) {
      this.selectedDeleteIds = checked ? this.deletableRows.map((assignment) => assignment.id) : []
    },

    confirmDeleteSelected() {
      const count = this.selectedDeleteIds.length
      if (!count || this.deletingSelected) {
        return
      }
      this.$dialog.confirm({
        title: this.$t('delete_assignments'),
        message: this.$t('delete_selected_assignments_confirm_text', { count }),
        confirmText: this.$t('delete_selected_assignments'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteSelected(),
      })
    },

    /**
     * Mismo endpoint que assignment-update-modal, una petición por medición. En serie porque
     * cada borrado recalcula el progreso de la actividad.
     */
    async deleteSelected() {
      const ids = [...this.selectedDeleteIds]
      if (!ids.length) {
        return
      }
      this.deletingSelected = true
      let deleted = 0
      try {
        for (const id of ids) {
          try {
            const { data } = await axios.post(`/api/v2/assignment/${id}/delete`)
            if (data && data.success) {
              deleted++
            }
          } catch (error) {
            // se contabiliza como fallida y se continúa con el resto
          }
        }

        const failed = ids.length - deleted
        if (failed) {
          this.$toast.open({
            message: this.$t('delete_selected_assignments_error', { failed, total: ids.length }),
            type: 'is-danger',
            position: 'is-top-right',
          })
        } else {
          this.$toast.open({
            message: this.$t('delete_selected_assignments_success', { count: deleted }),
            type: 'is-success',
            position: 'is-top-right',
          })
        }

        this.selectedDeleteIds = []
        if (deleted) {
          await this.reloadAssignments({ silent: true })
          this.$emit('deleted', { activityId: this.activityId, count: deleted })
        }
      } finally {
        this.deletingSelected = false
      }
    },

    releaseContributeDrawingWatchers() {
      if (this._releaseContributeDrawing) {
        this._releaseContributeDrawing()
        this._releaseContributeDrawing = null
      }
    },

    notifyContributeProgressSaved(assignment) {
      EventBus.$emit('refreshAssignmentChangedNotifications', {
        activityId: this.activityId,
        assignmentId: assignment.id,
      })
      EventBus.$emit('refreshActivitiesNotifications', true)
      this.onProgressRegistered()
    },
  },
}
</script>

<style scoped>
.weekly-activity-assignments-table__edit-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #fffbeb;
  border-bottom: 1px solid #fcd34d;
}

.weekly-activity-assignments-table__edit-total {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 700;
}

.weekly-activity-assignments-table__edit-input {
  min-width: 5.5rem;
  max-width: 7rem;
  margin: 0 auto;
}

.weekly-activity-assignments-table__delete-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #fef2f2;
  border-bottom: 1px solid #fca5a5;
}

.weekly-activity-assignments-table__delete-count {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #b91c1c;
}

.weekly-activity-assignments-table__delete-check {
  margin-right: 0 !important;
}

.weekly-activity-assignments-table__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #eef3fb;
  border-bottom: 1px solid #c5d4eb;
}

.weekly-activity-assignments-table__level-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  background-color: #2c426c;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
}

.weekly-activity-assignments-table__count {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3b66bc;
}

.weekly-activity-assignments-table__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid #3b66bc;
  border-radius: 0.25rem;
  background-color: #fff;
  color: #3b66bc;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-activity-assignments-table__add-btn:hover {
  background-color: #eef3fb;
}

.weekly-activity-assignments-table__add-btn--engineering {
  border-color: #3b66bc;
  color: #3b66bc;
}

.weekly-activity-assignments-table__body {
  min-height: 0;
  padding: 0.75rem;
}

.weekly-activity-assignments-table__section {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  background-color: #fff;
}

.weekly-activity-assignments-table__section--nested {
  border: none;
  border-radius: 0;
}

.weekly-activity-assignments-table__section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(90deg, #2c426c 0%, #3b66bc 100%);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.weekly-activity-assignments-table__section-icon {
  flex-shrink: 0;
}

.weekly-activity-assignments-table__section-title {
  flex: 1;
  min-width: 0;
}

.weekly-activity-assignments-table__header {
  padding: 0.375rem 0.5rem;
  text-align: center;
  font-weight: 600;
  color: #fff;
  background-color: #5b80be;
  border: 1px solid #5b80be;
  white-space: nowrap;
}

.weekly-activity-assignments-table__header--left {
  text-align: left;
}

.weekly-activity-assignments-table__expand-col,
.weekly-activity-assignments-table__actions-col {
  min-width: 2.5rem;
  width: 2.5rem;
}

.weekly-activity-assignments-table__cell {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  color: #1f2937;
  vertical-align: middle;
  background-color: #fff;
}

.weekly-activity-assignments-table__slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}

.weekly-activity-assignments-table__slot-icon--imported {
  color: #7c3aed;
}

.weekly-activity-assignments-table__slot-icon--manual {
  color: #3b66bc;
}

.weekly-activity-assignments-table__slot-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: #3b66bc;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.weekly-activity-assignments-table__slot-badge--imported {
  background-color: #7c3aed;
}

.weekly-activity-assignments-table__slot-badge--manual {
  background-color: #3b66bc;
}

.weekly-activity-assignments-table__row--imported > .weekly-activity-assignments-table__expand-col {
  box-shadow: inset 3px 0 0 #7c3aed;
}

.weekly-activity-assignments-table__code-link {
  color: #3b66bc;
  font-weight: 600;
  text-decoration: none;
}

.weekly-activity-assignments-table__code-link:hover {
  text-decoration: underline;
}

.weekly-activity-assignments-table__name-cell {
  max-width: 14rem;
}

.weekly-activity-assignments-table__name-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.weekly-activity-assignments-table__progress-cell {
  min-width: 8.5rem;
  width: 8.5rem;
}

.weekly-activity-assignments-table__kpi-theoretical {
  font-weight: 600;
  color: #2d6a3e;
}

.weekly-activity-assignments-table__kpi-real {
  font-weight: 600;
  color: #b45309;
}

.weekly-activity-assignments-table__complete-icon--yes {
  color: #38c172;
}

.weekly-activity-assignments-table__complete-icon--no {
  color: #e3342f;
}

.weekly-activity-assignments-table__empty {
  color: #c4c4c4;
}

.weekly-activity-assignments-table__row--expanded > .weekly-activity-assignments-table__cell {
  background-color: #eef3fb;
}

.weekly-activity-assignments-table__detail-cell {
  border: none !important;
  padding: 0 !important;
}

.weekly-activity-assignments-table__detail-panel {
  border-left: 3px solid #5b80be;
  background-color: #f8fafc;
}

.weekly-activity-assignments-table--activity-mode .weekly-activity-assignments-table__toolbar {
  background-color: #eef3fb;
}

/* Tema verde — nivel 3 subactividad */
.weekly-activity-assignments-table--green .weekly-activity-assignments-table__toolbar {
  background-color: #e8f5ec;
  border-bottom-color: #b8dcc4;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__level-badge {
  background-color: #2d6a3e;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__count {
  color: #2d6a3e;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__add-btn {
  border-color: #2d6a3e;
  color: #2d6a3e;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__add-btn:hover {
  background-color: #e8f5ec;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__header {
  background-color: #3d8b5a;
  border-color: #3d8b5a;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__slot-badge--manual,
.weekly-activity-assignments-table--green .weekly-activity-assignments-table__slot-badge:not(.weekly-activity-assignments-table__slot-badge--imported) {
  background-color: #2d6a3e;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__slot-icon--manual {
  color: #2d6a3e;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__code-link {
  color: #2d6a3e;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__row--expanded > .weekly-activity-assignments-table__cell {
  background-color: #e8f5ec;
}

.weekly-activity-assignments-table--green .weekly-activity-assignments-table__detail-panel {
  border-left-color: #3d8b5a;
}
</style>

<style>
body > .autocomplete.control,
body > .datepicker.control,
body > div > .dropdown .dropdown-menu {
  z-index: 120 !important;
}
</style>
